/**
 * Server-rendered page metadata for TanStack Router `head()`.
 *
 * Everything here ends up in the SSR HTML, which is what crawlers, social
 * scrapers and answer engines read. Client-only metadata (react-helmet-async)
 * never reached them, so page-level SEO lives in route `head()` instead.
 *
 * Rules:
 * - canonical is emitted on leaf routes only (router concatenates `links`).
 * - `og:url` mirrors the canonical so both self-reference the page.
 * - title is a `meta` entry (TanStack has no top-level `title` field).
 * - JSON-LD is emitted as ONE @graph per route (see ./graph).
 */
import { siteGraph } from "./graph";

export const SITE_BASE_URL = "https://leadxkids.com";
export const DEFAULT_OG_IMAGE = `${SITE_BASE_URL}/leadxkids-social-banner-2026-10.jpg`;

export const DEFAULT_TITLE =
  "LeadXkids | Life Skills & Confidence for Kids 8-14";
export const DEFAULT_DESCRIPTION =
  "Online life skills course for kids aged 8-14 in India: confidence, communication, focus and emotional skills.";


export interface PageSeo {
  /** Page title without the brand suffix (added automatically). */
  title?: string;
  description?: string;
  /** Site-relative path, e.g. "/blog/slug". Omit for noindex/utility routes. */
  canonical?: string;
  ogType?: string;
  /** schema.org page type for the WebPage node, e.g. "AboutPage". */
  pageType?: string;
  /** Absolute https URL. */
  ogImage?: string;
  noindex?: boolean;
  /** With noindex: let crawlers follow links ("noindex, follow"). */
  follow?: boolean;
  /**
   * Current request path (e.g. `match.pathname` from the route's head()).
   * Used as the canonical / og:url fallback when `canonical` is omitted on an
   * indexable page. Routes that canonicalise elsewhere (e.g. /blog → /learn)
   * pass an explicit `canonical`, which always wins.
   */
  path?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    authorName?: string;
    section?: string;
  };
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown> | null | undefined>;
}

type Meta = Record<string, string>;

export interface HeadResult {
  meta: Meta[];
  links: { rel: string; href: string }[];
  scripts: { type: string; children: string }[];
}

/** Build a TanStack `head()` payload from a page-level SEO descriptor. */
export function seoHead(seo: PageSeo = {}): HeadResult {
  // Brand suffix only while the full title still fits Google's ~60-char limit.
  const fullTitle = seo.title
    ? seo.title.length + 12 <= 60
      ? `${seo.title} | LeadXkids`
      : seo.title
    : DEFAULT_TITLE;
  const description = seo.description || DEFAULT_DESCRIPTION;
  const ogImage = seo.ogImage || DEFAULT_OG_IMAGE;
  const canonicalPath = seo.noindex ? undefined : seo.canonical || seo.path;
  const canonicalUrl = canonicalPath ? `${SITE_BASE_URL}${canonicalPath}` : undefined;

  const meta: Meta[] = [
    { title: fullTitle },
    { name: "description", content: description },
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: description },
    { property: "og:type", content: seo.ogType || "website" },
    { property: "og:image", content: ogImage },
    { property: "og:image:type", content: "image/jpeg" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:site_name", content: "LeadXkids" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: ogImage },
  ];

  if (seo.noindex) meta.push({ name: "robots", content: seo.follow ? "noindex, follow" : "noindex, nofollow" });
  if (canonicalUrl) meta.push({ property: "og:url", content: canonicalUrl });

  if ((seo.ogType || "") === "article" && seo.article) {
    const a = seo.article;
    if (a.publishedTime)
      meta.push({ property: "article:published_time", content: a.publishedTime });
    if (a.modifiedTime)
      meta.push({ property: "article:modified_time", content: a.modifiedTime });
    if (a.authorName) meta.push({ property: "article:author", content: a.authorName });
    if (a.section) meta.push({ property: "article:section", content: a.section });
  }

  const blocks = (Array.isArray(seo.jsonLd) ? seo.jsonLd : seo.jsonLd ? [seo.jsonLd] : [])
    .filter((b): b is Record<string, unknown> => Boolean(b));

  // One @graph per route: Organization / founder / WebSite / WebPage plus the
  // page-specific nodes, all cross-referenced by @id. Noindexed utility routes
  // get no structured data at all.
  const graph = seo.noindex
    ? null
    : siteGraph(
        canonicalUrl
          ? {
              url: canonicalUrl,
              name: fullTitle,
              description,
              image: ogImage,
              ogType: seo.ogType,
              pageType: seo.pageType,
            }
          : null,
        blocks,
      );

  return {
    meta,
    links: canonicalUrl ? [{ rel: "canonical", href: canonicalUrl }] : [],
    scripts: graph
      ? [{ type: "application/ld+json", children: JSON.stringify(graph) }]
      : [],
  };
}

/**
 * Marks a document rendered from fallback data (slow/failed lookup) so the HTML
 * page cache never stores it. Matched by `isDegradedHtml` in htmlEdgeCache.
 */
export const DEGRADED_META = { name: "lx-render", content: "degraded" } as const;
export function markDegraded(head: HeadResult, degraded: boolean): HeadResult {
  return degraded ? { ...head, meta: [...head.meta, { ...DEGRADED_META }] } : head;
}

/** Shorthand for private/utility routes that should never be indexed. */
export function noindexHead(title: string): HeadResult {
  return seoHead({ title, noindex: true });
}
